import { useCallback, useRef } from 'react';
import LogicFlow from '@logicflow/core';
import { nodeTypes } from '../types/nodeTypes';
import type { OntologyGraphLayer } from '../../../types/opm';
import { Modal } from 'antd';

interface GraphData {
  nodes: any[];
  edges: any[];
  _timestamp?: number;
}

interface ElementHistory {
  id: string;
  type: 'node' | 'edge';
  timestamp: number;
}

interface UseOPMOperationsProps {
  lfRef: React.RefObject<LogicFlow | null>;
  setCurrentGraphData: (data: GraphData) => void;
  setNodeCounter: (counter: number | ((prev: number) => number)) => void;
  setGlobalElementOrder: (order: number | ((prev: number) => number)) => void;
  setElementAddHistory: (
    history: ElementHistory[] | ((prev: ElementHistory[]) => ElementHistory[])
  ) => void;
  getCurrentLayerMeta?: () =>
    | { nodeIdPrefix?: string; parentNodeId?: string }
    | undefined;
  getChildLayersByNodeId?: (nodeId: string) => OntologyGraphLayer[];
  removeChildLayersByNodeId?: (nodeId: string) => void;
}

export const useOPMOperations = ({
  lfRef,
  setCurrentGraphData,
  setNodeCounter,
  setGlobalElementOrder,
  setElementAddHistory,
  getCurrentLayerMeta,
  getChildLayersByNodeId,
  removeChildLayersByNodeId,
}: UseOPMOperationsProps) => {
  // 使用 ref 来同步追踪计数器，避免在 setState 回调中执行副作用
  const nodeCounterRef = useRef(0);
  const globalElementOrderRef = useRef(0);

  // 更新图数据防抖定时器
  const updateDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 上次更新的时间戳，用于避免重复更新
  const lastUpdateTimeRef = useRef<number>(0);
  // 待更新标志：如果在防抖期间有新的更新请求，记录下来
  const pendingUpdateRef = useRef<boolean>(false);
  // 交互状态标志：拖动期间跳过更新
  const isInteractingRef = useRef<boolean>(false);

  // 设置交互状态 - 不再自动触发 updateGraphData
  // 画布拖动不改变数据，不需要更新
  const setInteracting = useCallback((interacting: boolean) => {
    isInteractingRef.current = interacting;
    // 不再自动执行 updateGraphData
    // 只有显式调用 updateGraphData 时才会更新
  }, []);

  // 内部更新函数 - 使用 requestIdleCallback 在浏览器空闲时执行
  const updateGraphDataInternal = useCallback(() => {
    if (!lfRef.current) return;

    const doUpdate = () => {
      if (!lfRef.current) return;

      const startTime = performance.now();
      const graphData = lfRef.current.getGraphData() as {
        nodes: any[];
        edges: any[];
      };
      const getDataTime = performance.now() - startTime;

      setCurrentGraphData({
        ...graphData,
        _timestamp: Date.now(),
      });

      lastUpdateTimeRef.current = Date.now();
      pendingUpdateRef.current = false;
      const totalTime = performance.now() - startTime;

      if (totalTime > 100) {
        console.warn(
          `⚠️ updateGraphData 耗时较长: 节点数=${graphData.nodes.length}, 边数=${graphData.edges.length}, getGraphData=${getDataTime.toFixed(1)}ms, 总计=${totalTime.toFixed(1)}ms`
        );
      }
    };

    // 使用 requestIdleCallback 在浏览器空闲时执行，避免阻塞主线程
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(doUpdate, { timeout: 2000 });
    } else {
      // 回退到 setTimeout
      setTimeout(doUpdate, 100);
    }
  }, [lfRef, setCurrentGraphData]);

  // 更新图数据 - 简化版本
  // 核心优化：交互期间跳过 + 频率限制 + requestIdleCallback
  const updateGraphData = useCallback(() => {
    if (!lfRef.current) return;

    // 交互期间完全跳过
    if (isInteractingRef.current) {
      pendingUpdateRef.current = true;
      return;
    }

    const now = Date.now();
    // 频率限制：2秒内只允许一次更新
    if (now - lastUpdateTimeRef.current < 2000) {
      pendingUpdateRef.current = true;
      // 设置延迟执行
      if (updateDebounceRef.current) {
        clearTimeout(updateDebounceRef.current);
      }
      updateDebounceRef.current = setTimeout(() => {
        if (pendingUpdateRef.current && !isInteractingRef.current) {
          updateGraphDataInternal();
        }
      }, 2000);
      return;
    }

    // 清除之前的防抖定时器
    if (updateDebounceRef.current) {
      clearTimeout(updateDebounceRef.current);
    }

    // 使用防抖 + requestIdleCallback
    updateDebounceRef.current = setTimeout(() => {
      if (!lfRef.current || isInteractingRef.current) {
        pendingUpdateRef.current = true;
        return;
      }
      updateGraphDataInternal();
    }, 500); // 500ms 防抖
  }, [lfRef, updateGraphDataInternal]);

  const withLayerPrefix = useCallback(
    (rawId: string) => {
      const prefix = getCurrentLayerMeta?.()?.nodeIdPrefix || '';
      return prefix ? `${prefix}__${rawId}` : rawId;
    },
    [getCurrentLayerMeta]
  );

  const isProtectedNode = useCallback(
    (node: any) => !!node?.properties?.isSubgraphRoot,
    []
  );

  const getNodeDisplayName = useCallback((nodeModel: any) => {
    if (!nodeModel) return '';
    const textValue =
      (typeof nodeModel.text === 'object'
        ? nodeModel.text?.value
        : nodeModel.text) ||
      nodeModel.properties?.name ||
      nodeModel.id;
    return textValue || nodeModel.id;
  }, []);

  const showRootDeleteWarning = useCallback(() => {
    Modal.warning({
      title: '无法删除节点',
      content: '当前子图的根节点不允许删除，请返回父图后再进行删除操作。',
      okText: '知道了',
      centered: true,
    });
  }, []);

  const confirmDeleteNodeWithChildren = useCallback(
    (nodeLabel: string, childLayers: OntologyGraphLayer[]) =>
      new Promise<boolean>(resolve => {
        Modal.confirm({
          title: `删除节点「${nodeLabel || '节点'}」`,
          centered: true,
          okText: '删除',
          okType: 'danger',
          cancelText: '取消',
          content: (
            <div className="subgraph-delete-confirm">
              <p>该节点已创建以下子图，删除后这些子图将被同时移除：</p>
              <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
                {childLayers.map(layer => (
                  <li key={layer.layerId}>{layer.layerName}</li>
                ))}
              </ul>
              <p style={{ color: '#f5222d', marginBottom: 0 }}>
                此操作不可撤销，请确认是否继续。
              </p>
            </div>
          ),
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      }),
    []
  );

  const deleteNodeWithGuard = useCallback(
    async (nodeId: string) => {
      if (!lfRef.current) return false;
      const targetNode = lfRef.current.getNodeModelById(nodeId);
      if (!targetNode) return false;

      if (isProtectedNode(targetNode)) {
        showRootDeleteWarning();
        return false;
      }

      const childLayers = getChildLayersByNodeId?.(nodeId) || [];
      if (childLayers.length) {
        const confirmed = await confirmDeleteNodeWithChildren(
          getNodeDisplayName(targetNode),
          childLayers
        );
        if (!confirmed) {
          return false;
        }
        removeChildLayersByNodeId?.(nodeId);
      }

      lfRef.current.deleteNode(nodeId);
      return true;
    },
    [
      confirmDeleteNodeWithChildren,
      getChildLayersByNodeId,
      getNodeDisplayName,
      isProtectedNode,
      lfRef,
      removeChildLayersByNodeId,
      showRootDeleteWarning,
    ]
  );

  // 删除选中的元素
  const deleteSelectedElements = useCallback(() => {
    if (!lfRef.current) return;

    const performDelete = async () => {
      console.log('=== 开始删除选中元素 ===');

      // 使用LogicFlow的API获取选中元素
      let selectedElements = null;

      try {
        // 尝试不同的参数
        selectedElements = lfRef.current?.getSelectElements(true);
        console.log('getSelectElements(true) 结果:', selectedElements);
      } catch (e) {
        console.log('getSelectElements(true) 失败:', e);

        // 尝试无参数版本
        try {
          selectedElements = lfRef.current?.getSelectElements();
          console.log('getSelectElements() 结果:', selectedElements);
        } catch (e2) {
          console.log('getSelectElements() 也失败:', e2);
        }
      }

      if (
        !selectedElements ||
        (selectedElements.nodes?.length === 0 &&
          selectedElements.edges?.length === 0)
      ) {
        console.log('没有找到选中的元素');
        return;
      }

      console.log('准备删除选中的元素:', selectedElements);

      if (selectedElements.nodes?.length) {
        for (const node of selectedElements.nodes) {
          // eslint-disable-next-line no-await-in-loop
          await deleteNodeWithGuard(node.id);
        }
      }

      // 删除选中的边
      selectedElements.edges?.forEach((edge: any) => {
        console.log('🗑️ 删除边:', edge.id);
        lfRef.current?.deleteEdge(edge.id);
      });

      // 延迟更新，确保所有删除事件都已处理完成
      setTimeout(() => {
        console.log('⏰ 延迟更新图数据（删除操作后）');
        updateGraphData();
      }, 200);

      console.log(
        `删除完成: ${selectedElements.nodes?.length || 0}个节点, ${selectedElements.edges?.length || 0}条边`
      );
    };

    performDelete().catch(error => {
      console.error('删除选中元素时出错:', error);
    });
  }, [deleteNodeWithGuard, lfRef, updateGraphData]);

  // 清空画布
  const clearGraph = useCallback(() => {
    if (!lfRef.current) return;

    const layerMeta = getCurrentLayerMeta?.();
    if (layerMeta?.parentNodeId) {
      const graphData = lfRef.current.getGraphData() as {
        nodes: any[];
        edges: any[];
      };
      const rootNode = graphData.nodes.find(
        node => node.id === layerMeta.parentNodeId
      );
      if (!rootNode) {
        console.warn('未找到子图根节点，无法清空。');
        return;
      }
      lfRef.current.render({
        nodes: [rootNode],
        edges: [],
      });
      console.log('仅保留子图根节点，其余内容已清空');
      updateGraphData();
      return;
    }

    lfRef.current.clearData();
    setNodeCounter(0);
    console.log('画布已清空');
  }, [getCurrentLayerMeta, lfRef, setNodeCounter, updateGraphData]);

  // 获取图数据
  const getGraphData = useCallback(() => {
    if (!lfRef.current) return;

    const data = lfRef.current.getGraphData();
    console.log('当前图数据:', data);
    return data;
  }, [lfRef]);

  // 添加节点
  const addNode = useCallback(
    (type: string = 'rect') => {
      if (!lfRef.current) return;

      // 使用 ref 同步更新计数器，避免在 setState 回调中执行副作用
      nodeCounterRef.current += 1;
      globalElementOrderRef.current += 1;

      const newCounter = nodeCounterRef.current;
      const newOrder = globalElementOrderRef.current;
      const nodeId = withLayerPrefix(`node_${newCounter}`);

      const nodeConfig = nodeTypes.find(n => n.type === type);
      const textValue = nodeConfig
        ? `${nodeConfig.name}${newCounter}`
        : `节点${newCounter}`;

      const node = {
        id: nodeId,
        type: type,
        x: 100 + (newCounter - 1) * 150,
        y: 100 + Math.floor((newCounter - 1) / 3) * 100,
        text: textValue,
        properties: {
          name: textValue,
          description: `这是第${newCounter}个${nodeConfig?.name || '节点'}`,
          nodeType: type,
        },
      };

      // 先创建节点（副作用）
      lfRef.current.addNode(node);

      // 然后同步状态（用于 UI 渲染）
      setNodeCounter(newCounter);
      setGlobalElementOrder(newOrder);
      setElementAddHistory(prev => {
        if (!prev.find(h => h.id === nodeId)) {
          return [
            ...prev,
            {
              id: nodeId,
              type: 'node',
              timestamp: newOrder,
            },
          ];
        }
        return prev;
      });
    },
    [
      lfRef,
      setNodeCounter,
      setGlobalElementOrder,
      setElementAddHistory,
      withLayerPrefix,
    ]
  );

  // 在指定位置创建节点
  const createNodeAtPosition = useCallback(
    (type: string, x: number, y: number) => {
      if (!lfRef.current) return;

      // 使用 ref 同步更新计数器，避免在 setState 回调中执行副作用
      nodeCounterRef.current += 1;
      globalElementOrderRef.current += 1;

      const newCounter = nodeCounterRef.current;
      const newOrder = globalElementOrderRef.current;
      const nodeId = withLayerPrefix(`node_${newCounter}`);

      const nodeConfig = nodeTypes.find(n => n.type === type);
      const textValue = nodeConfig
        ? `${nodeConfig.name}${newCounter}`
        : `节点${newCounter}`;

      const node = {
        id: nodeId,
        type: type,
        x: x,
        y: y,
        text: textValue,
        properties: {
          name: textValue,
          description: `这是第${newCounter}个${nodeConfig?.name || '节点'}`,
          nodeType: type,
        },
      };

      // 先创建节点（副作用）
      lfRef.current.addNode(node);

      // 然后同步状态（用于 UI 渲染）
      setNodeCounter(newCounter);
      setGlobalElementOrder(newOrder);
      setElementAddHistory(prev => {
        if (!prev.find(h => h.id === nodeId)) {
          return [
            ...prev,
            {
              id: nodeId,
              type: 'node',
              timestamp: newOrder,
            },
          ];
        }
        return prev;
      });
    },
    [
      lfRef,
      setNodeCounter,
      setGlobalElementOrder,
      setElementAddHistory,
      withLayerPrefix,
    ]
  );

  // 随机创建节点（测试功能）
  const createRandomNodes = useCallback(() => {
    if (!lfRef.current) return;

    const nodeTypeList = ['rect', 'circle', 'diamond', 'polygon'];
    const nodeNames = [
      '用户',
      '订单',
      '商品',
      '支付',
      '库存',
      '配送',
      '评价',
      '客服',
      '登录',
      '注册',
      '搜索',
      '购买',
      '退款',
      '发货',
      '收货',
      '评分',
    ];

    // 随机创建5个节点
    for (let i = 0; i < 5; i++) {
      const randomType =
        nodeTypeList[Math.floor(Math.random() * nodeTypeList.length)];
      const randomName =
        nodeNames[Math.floor(Math.random() * nodeNames.length)];
      const x = 100 + Math.random() * 600;
      const y = 100 + Math.random() * 300;

      const newCounter = 0; // 这里需要获取当前的计数器值
      const nodeConfig = nodeTypes.find(n => n.type === randomType);

      const node = {
        id: withLayerPrefix(`node_${Date.now()}_${i}`),
        type: randomType,
        x: x,
        y: y,
        text: `${randomName}_${i}`,
        properties: {
          name: `${randomName}_${i}`,
          description: `这是第${i}个${nodeConfig?.name || '节点'}`,
          nodeType: randomType,
        },
      };

      lfRef.current.addNode(node);
    }

    console.log('随机创建了5个节点');
  }, [lfRef, withLayerPrefix]);

  // 创建示例 OPM 图（测试功能）
  const createSampleOPMDiagram = useCallback(() => {
    if (!lfRef.current) return;

    // 清空现有内容
    lfRef.current.clearData();
    setNodeCounter(0);

    // 创建示例节点
    const sampleNodes = [
      {
        id: 'user',
        type: 'dynamic-group', // 用户实体作为动态分组，包含状态
        x: 150,
        y: 120,
        text: '用户',
        width: 160,
        height: 140,
        properties: {
          name: '用户',
          description: '系统用户实体',
          nodeType: 'rect',
          isGroup: true,
          isEntityGroup: true,
          originalType: 'rect',
          states: ['user_state_1', 'user_state_2'],
          isRestrict: true, // 启用限制模式
          autoResize: false, // 启用自动调整大小
        },
      },
      {
        id: 'user_state_1',
        type: 'state',
        x: 120,
        y: 110,
        text: '在线',
        width: 70,
        height: 30,
        properties: {
          name: '在线',
          description: '用户的在线状态',
          nodeType: 'state',
          parentGroupId: 'user',
          isState: true,
        },
      },
      {
        id: 'user_state_2',
        type: 'state',
        x: 180,
        y: 110,
        text: '离线',
        width: 70,
        height: 30,
        properties: {
          name: '离线',
          description: '用户的离线状态',
          nodeType: 'state',
          parentGroupId: 'user',
          isState: true,
        },
      },
      {
        id: 'order',
        type: 'rect',
        x: 400,
        y: 100,
        text: '订单',
        properties: {
          name: '订单',
          description: '订单实体',
          nodeType: 'rect',
        },
      },
      {
        id: 'product',
        type: 'rect',
        x: 650,
        y: 100,
        text: '商品',
        properties: {
          name: '商品',
          description: '商品实体',
          nodeType: 'rect',
        },
      },
      {
        id: 'payment',
        type: 'rect',
        x: 400,
        y: 300,
        text: '支付',
        properties: {
          name: '支付',
          description: '支付实体',
          nodeType: 'rect',
        },
      },

      {
        id: 'username',
        type: 'circle',
        x: 150,
        y: 200,
        text: '用户名',
        properties: {
          name: '用户名',
          description: '用户名属性',
          nodeType: 'circle',
        },
      },
      {
        id: 'email',
        type: 'circle',
        x: 50,
        y: 150,
        text: '邮箱',
        properties: {
          name: '邮箱',
          description: '邮箱属性',
          nodeType: 'circle',
        },
      },
      {
        id: 'price',
        type: 'circle',
        x: 650,
        y: 200,
        text: '价格',
        properties: {
          name: '价格',
          description: '商品价格属性',
          nodeType: 'circle',
        },
      },

      {
        id: 'creates',
        type: 'diamond',
        x: 275,
        y: 100,
        text: '创建',
        properties: {
          name: '创建',
          description: '用户创建订单关系',
          nodeType: 'diamond',
        },
      },
      {
        id: 'contains',
        type: 'diamond',
        x: 525,
        y: 100,
        text: '包含',
        properties: {
          name: '包含',
          description: '订单包含商品关系',
          nodeType: 'diamond',
        },
      },

      {
        id: 'checkout',
        type: 'polygon',
        x: 275,
        y: 250,
        text: '结账过程',
        properties: {
          name: '结账过程',
          description: '用户结账过程',
          nodeType: 'polygon',
        },
      },
      {
        id: 'shipping',
        type: 'polygon',
        x: 525,
        y: 250,
        text: '发货过程',
        properties: {
          name: '发货过程',
          description: '商品发货过程',
          nodeType: 'polygon',
        },
      },
    ];

    // 添加节点
    sampleNodes.forEach(node => {
      lfRef.current?.addNode({
        id: node.id,
        type: node.type,
        x: node.x,
        y: node.y,
        text: node.text,
        width: node.width,
        height: node.height,
        properties: node.properties,
      });
    });

    // 创建示例连接
    const sampleEdges = [
      { source: 'user', target: 'creates', type: 'polyline', text: '执行' },
      {
        source: 'creates',
        target: 'order',
        type: 'triangle-filled',
        text: '产生',
      },
      { source: 'order', target: 'contains', type: 'polyline', text: '关联' },
      {
        source: 'contains',
        target: 'product',
        type: 'triangle-hollow',
        text: '包含',
      },

      {
        source: 'user',
        target: 'username',
        type: 'circle-filled',
        text: '具有',
      },
      { source: 'user', target: 'email', type: 'circle-hollow', text: '拥有' },
      {
        source: 'product',
        target: 'price',
        type: 'diamond-filled',
        text: '属性',
      },

      { source: 'user', target: 'checkout', type: 'polyline', text: '触发' },
      {
        source: 'checkout',
        target: 'payment',
        type: 'triangle-filled',
        text: '生成',
      },
      { source: 'order', target: 'shipping', type: 'polyline', text: '启动' },
      {
        source: 'shipping',
        target: 'product',
        type: 'triangle-hollow',
        text: '处理',
      },
    ];

    // 添加连接（延迟添加以确保节点已创建）
    setTimeout(() => {
      sampleEdges.forEach(edge => {
        lfRef.current?.addEdge({
          sourceNodeId: edge.source,
          targetNodeId: edge.target,
          type: edge.type,
          text: edge.text,
          properties: {
            edgeType: edge.type,
            text: edge.text,
          },
        });
      });

      console.log('示例 OPM 图创建完成');
    }, 100);

    setNodeCounter(sampleNodes.length);
  }, [lfRef, setNodeCounter]);

  return {
    updateGraphData,
    setInteracting, // 设置交互状态，拖动期间跳过 updateGraphData
    deleteSelectedElements,
    clearGraph,
    getGraphData,
    addNode,
    createNodeAtPosition,
    createRandomNodes,
    createSampleOPMDiagram,
    deleteNodeWithGuard,
  };
};
